import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { ChangeEvent, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteBin6LineIcon,
  EditLineIcon,
  Information2LineIcon,
  RotationLock,
  SortAscIcon,
  SortDescIcon,
  UserSettingIcon,
} from '~/assets/icons';
import styles from '../User.module.scss';
import { IUser } from '~/utils/interface/user';
import { cast } from '~/utils/helper';
import { SEARCH_TIMEOUT } from '~/utils/constants/common';
const cx = classNames.bind(styles);

type Props = {
  pageSize: number;
  users: IUser[];
  form: any;
  onDetail?: (row: IUser) => void;
  onEdit?: (row: IUser) => void;
  onReset?: (row: IUser) => void;
  onAssign?: (row: IUser) => void;
  onDelete?: (row: IUser) => void;
  userIds?: Array<string>;
  handleCheck?: (row: IUser, checked: boolean) => void;
  handleCheckAll?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
};

const UserTable = (props: Props) => {
  const { t } = useTranslation('admin/user');
  const { userIds, handleCheck, handleCheckAll } = props;

  const searchTimeout = useRef<any>(null);

  const getStatus = (status: number) => {
    if (status === 0) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] h-[25px] p-[2px]'>{t('status.new')}</Box>
      );
    }

    if (status === 1) {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px]  h-[25px] p-[2px]'>
          {t('status.active')}
        </Box>
      );
    }
    if (status === 2) {
      return (
        <Box className='text-[#005F73] border border-[#005F73] rounded-[20px] h-[25px] p-[2px]'>
          {t('status.friendly')}
        </Box>
      );
    }
    if (status === 3) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] h-[25px] p-[2px]'>
          {t('status.block')}
        </Box>
      );
    }

    return '';
  };

  const getFeedback = (value: number) => {
    if (value === 0) return t('feedback.no');
    else if (value === 1) return t('feedback.yes');
    else return '';
  };

  const handleSelectedOptions = (field: string, value: any) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      props.form.setFieldValue(field, value);
    }, SEARCH_TIMEOUT);
  };

  const handleSort = (field: string, type: string) => {
    props.form.setFieldValue('order_by', { field, type });
  };

  const convertToToFeedbackNumber = (value: any) => {
    const data: any = value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (['k', 'h', 'o', 'n', 'g', 'khong', 'khon', 'kh', 'kho'].includes(data)) return [0];
    if (['c', 'o', 'co'].includes(data)) return [1];

    return [''];
  };

  return (
    <TableContainer
      sx={{
        border: '1px solid #D9D9D9',
        borderRadius: '1px',
        maxHeight: '100vh',
      }}
    >
      <Table stickyHeader>
        <TableHead className='bg-[#005F73]'>
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                onChange={handleCheckAll}
                sx={{
                  color: 'white',
                  '&.Mui-checked': {
                    color: 'white',
                  },
                }}
              />
            </TableCell>

            <TableCell className={cx('min-w-[50px]', 'table-header')} align='center'>
              {t('list.no')}
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[140px]'>
              <Box className={cx('header-title-container')}>
                <Typography className={cx('header-title')}>{t('list.code')}</Typography>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'username' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('username', 'desc');
                    }}
                  />
                ) : (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('username', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.username}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('username', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('list.fullName')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'name' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('name', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('name', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.name}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('name', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('list.numberFacility')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'total_locations' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('total_locations', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('total_locations', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.total_locations}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('total_locations', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('list.numberEquipment')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'total_devices' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('total_devices', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('total_devices', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.total_devices}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('total_devices', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('list.createdAt')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'created_at' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('created_at', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('created_at', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.created_at}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('created_at', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('list.feedback')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'feedback' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('feedback', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('feedback', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.feedback}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('feedback', convertToToFeedbackNumber(e.target.value))}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('list.technician')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'technician' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('technician', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('technician', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.technician}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('technician', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('list.status')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'user_status' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('user_status', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('user_status', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>

            <TableCell className={cx('w-[150px]', 'table-header')} align='center'>
              {t('action')}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {props.users?.map((row, index) => (
            <TableRow key={index}>
              <TableCell padding='checkbox'>
                <Checkbox
                  checked={(userIds ?? []).find((id) => id === row.username) ? true : false}
                  onChange={(event, checked) => handleCheck && handleCheck(row, checked)}
                  sx={{
                    color: '#005F73',
                    '&.Mui-checked': {
                      color: '#005F73',
                    },
                  }}
                />
              </TableCell>
              <TableCell align='center'>{index + 1 + (props.form.values.page_index - 1) * props.pageSize}</TableCell>
              <TableCell align='center'>{cast(row.username)}</TableCell>
              <TableCell align='center'>{cast(row.name)}</TableCell>
              <TableCell align='center'>{row.total_locations}</TableCell>
              <TableCell align='center'>{row.total_devices}</TableCell>
              <TableCell align='center'>{row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY') : ''}</TableCell>
              <TableCell align='center'>{getFeedback(cast(row.feedback))}</TableCell>
              <TableCell align='center'>{cast(row.technician)}</TableCell>
              <TableCell align='center'>{getStatus(cast(row.user_status))}</TableCell>
              <TableCell align='center'>
                <Box className='flex'>
                  <IconButton className='p-0' onClick={() => props?.onDetail && props.onDetail(row)}>
                    <Information2LineIcon fill='#72777B' />
                  </IconButton>
                  <IconButton className='py-0' onClick={() => props?.onEdit && props.onEdit(row)}>
                    <EditLineIcon />
                  </IconButton>
                  <IconButton className='p-0' onClick={() => props.onReset && props.onReset(row)}>
                    <RotationLock fill='#ED1002' />
                  </IconButton>
                  <IconButton className='py-0' onClick={() => props.onAssign && props.onAssign(row)}>
                    <UserSettingIcon className='w-4' fill='#005F73' />
                  </IconButton>
                  <IconButton className='p-0' onClick={() => props?.onDelete && props.onDelete(row)}>
                    <DeleteBin6LineIcon fill='#ED1002' />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(UserTable);
